<template>
  <v-dialog v-model="dialog" scrollable fullscreen hide-overlay>
    <v-card>
      <v-toolbar color="primary" dark tabs>
        <v-toolbar-title>
          {{
            selected._id
              ? $t('titles.global.updateModel', { field: $tc('models.match') })
              : $t('titles.global.newModel', { field: $tc('models.match') })
          }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn
          v-if="selected._id"
          icon
          dark
          :loading="$store.state.matches.loading"
          @click="$store.dispatch('matches/fetchOne', selected._id)"
        >
          <v-icon>refresh</v-icon>
        </v-btn>
        <v-btn dark icon @click="cancel">
          <v-icon>close</v-icon>
        </v-btn>
        <v-tabs
          slot="extension"
          v-model="tab"
          color="primary"
          slider-color="white"
          centered
        >
          <v-tab>{{ $tc('models.match') }}</v-tab>
          <v-tab :disabled="!selected._id || !selected.homeTeamId">
            {{ $tc('titles.global.homeTeamLineUp') }}
          </v-tab>
          <v-tab :disabled="!selected._id || !selected.visitorTeamId">
            {{ $tc('titles.global.visitorTeamLineUp') }}
          </v-tab>
          <v-tab :disabled="!selected._id">
            {{ $tc('models.event', 2) }}
          </v-tab>
        </v-tabs>
      </v-toolbar>
      <v-card-text>
        <v-tabs-items v-model="tab">
          <v-tab-item ripple>
            <tab-information
              ref="tabInformation"
              :match.sync="match"
              :tournament-view="tournamentView"
              :tournament="tournament"
              :phase="phase"
              :group="group"
            />
          </v-tab-item>
          <v-tab-item ripple>
            <team-line-up
              v-if="selected._id && selected.homeTeamId"
              :match-id="selected._id"
              :team-id="selected.homeTeamId._id"
              team-type="home"
            />
          </v-tab-item>
          <v-tab-item ripple>
            <team-line-up
              v-if="selected._id && selected.visitorTeamId"
              :match-id="selected._id"
              :team-id="selected.visitorTeamId._id"
              team-type="visitor"
            />
          </v-tab-item>
          <v-tab-item ripple>
            <tab-events
              :match-id="selected._id"
              :location="match.location"
              :home-team-id="match.homeTeamId"
              :visitor-team-id="match.visitorTeamId"
              :video="video"
            />
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>
      <v-divider />
      <v-card-actions v-if="tab === 0">
        <span style="color: red">*</span>
        <span>{{ $t('action.messages.fieldRequired') }}</span>
        <v-spacer />
        <template>
          <template v-if="!match._id">
            <meta-button
              v-if="!loading"
              :disabled="loading"
              type="reset"
              @click="clear"
            />
            <meta-button type="create" :loading="loading" @click="submit()" />
          </template>
          <template v-else>
            <button-confirm
              v-if="canReset && can('edit', 'match', match)"
              color="error"
              :text="$t('action.buttons.resetMatch')"
              :text-info="$t('titles.matches.resetMatchInfo')"
              :disabled="loading"
              @submit="resetMatch(match._id)"
            />
            <meta-button
              v-if="can('edit', 'match', match)"
              type="update"
              :loading="loading"
              @click="submit()"
            />
          </template>
        </template>
        <meta-button type="cancel" @click="cancel" />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from 'moment'
import { mapActions, mapState } from 'vuex'

import TabEvents from './TabEvents'
import TabInformation from './TabInformation'
import TeamLineUp from './components/TeamLineUp'
import ButtonConfirm from '@/components/widgets/button/ButtonConfirm'
import { formatDatePicker } from '@/helpers/dateHelper'
import { parseObject } from '@/helpers/utils'

export default {
  name: 'MatchForm',
  components: {
    TabInformation,
    TabEvents,
    TeamLineUp,
    ButtonConfirm,
  },
  props: {
    dialog: {
      type: Boolean,
      required: true,
    },
    tournamentView: {
      type: Boolean,
      required: false,
      default: false,
    },
    tournament: {
      type: Object,
      required: false,
      default: null,
    },
    phase: {
      type: Object,
      required: false,
      default: null,
    },
    group: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      match: {},
      tab: 0,
    }
  },
  watch: {
    dialog(val) {
      if (!val) this.cancel()
    },
    selected: {
      handler: async function (val) {
        if (val) {
          await this.setObject(val)
        }
      },
      immediate: true,
      deep: true,
    },
  },
  async mounted() {
    await this.setObject(this.selected)
  },
  computed: {
    ...mapState({
      selected: (state) => state.matches.selected,
      loading: (state) => state.matches.loading,
      video: (state) => state.matches.video,
    }),
    canReset() {
      let canReset = true
      if (!this.match || typeof this.match !== 'object') {
        canReset = false
      } else if (this.match.started && !this.match.ended) {
        canReset = false
      } else if (this.match.streamingStarted && !this.match.streamingEnded) {
        canReset = false
      }
      return canReset && (this.isAdmin || this.match.expiredReset === false)
    },
  },
  methods: {
    ...mapActions({
      setCreate: 'matches/create',
      setUpdate: 'matches/update',
      reset: 'matches/reset',
      resetMatch: 'matches/resetMatch',
      fetchOne: 'matches/fetchOne',
      fetchOneTeam: 'teams/fetchOne',
      fetchUserSelect: 'users/fetchSelectList',
      resetTeam: 'teams/reset',
    }),
    submit() {
      this.$nextTick(() => {
        Promise.all([this.$refs.tabInformation.validate()]).then((valid) => {
          if (valid.some((valid) => !valid)) return
          let payload = Object.assign({}, this.match)
          if (payload.autoStreaming) {
            payload.dateAutoStreaming = moment(payload.date)

            let [hour, minute] = payload.timeAutoStreaming.split(':')
            payload.dateAutoStreaming.set({ hour, minute, second: 0 })
            // delete payload.timeAutoStreaming
          }
          if (!payload._id) {
            this.setCreate(payload).then((result) => {
              this.$emit('created', result)
              this.fetchOne(result._id)
              // this.cancel()
            })
          } else {
            this.setUpdate(payload).then((result) => {
              this.$emit('updated', result)
              this.fetchOne(result._id)
              // this.cancel()
            })
          }
        })
      })
    },
    async setObject(match) {
      this.match = parseObject(match)

      if (this.tournamentView && this.tournament) {
        this.match.type = 'tournament'
        this.match.tournamentId = this.tournament._id

        // if (this.group && this.group.pitch) {
        //   this.match.location = this.group.pitch._id
        // }
      }

      if (this.match.tournamentId) {
        this.$store.dispatch('tournaments/fetchSelectList', {
          _id: this.match.tournamentId,
        })
        this.$store.dispatch('teams/fetchSelectList', {
          tournamentId: this.match.tournamentId,
        })
      }
      if (match._id) {
        this.match.date = formatDatePicker(match.date)
        if (match.broadcastId) {
          this.match.broadcastId = match.broadcastId.toLocaleString(
            'fullwide',
            {
              useGrouping: false,
            }
          )
        }
        // if (match.dateAutoStreaming) {
        //   this.match.timeAutoStreaming = moment(match.dateAutoStreaming).format(
        //     'HH:mm'
        //   )
        // }

        if (this.match.homeTeamId || this.match.visitorTeamId) {
          let params = {
            _id: [],
          }
          if (this.match.homeTeamId) params._id.push(this.match.homeTeamId)
          if (this.match.visitorTeamId)
            params._id.push(this.match.visitorTeamId)

          let teams = await this.$store.dispatch(
            'teams/fetchSelectList',
            params
          )

          if (teams.length > 1) {
            this.match.homeEntityId = teams.find(
              (team) => team.value == this.match.homeTeamId
            ).entityId
            this.match.visitorEntityId = teams.find(
              (team) => team.value == this.match.visitorTeamId
            ).entityId
          } else if (teams.length == 1) {
            // == 1 , same club, same team
            this.match.homeEntityId = this.match.homeTeamId
              ? teams[0].entityId
              : null
            this.match.visitorEntityId = this.match.visitorTeamId
              ? teams[0].entityId
              : null
          } else {
            this.match.homeEntityId = null
            this.match.visitorEntityId = null
          }
        }
      }
    },

    clear() {
      this.reset()
      this.tab = 0
      this.$nextTick(() => {
        if (this.$refs.tabInformation) this.$refs.tabInformation.clear()
      })
    },
    cancel() {
      this.clear()
      this.$emit('close')
    },
  },
}
</script>
